// The kitting/assembly field set (mode, BOM picker, kit contents, the counts
// derived from them, the build recipe, packaging) + the recap builder the spec
// rail reads. ONE implementation, rendered from the ONE Assembly panel
// (AssemblyControl.tsx), which is mounted once per `assembly` item over that
// item's own snapshot at QuoteLine.config.assembly — the one assembly flow
// (ADR-020; ADR-018 Decision 2 as restated by ADR-019 Decision 1). How many kits
// is the item's own Quantity field, so nothing here asks a count. The labels
// flag, the per-pack count and the instructions box are authored by that panel
// instead, each beside the packing-detail axis it belongs with.
//
// Every input's DOM id equals its field key, so the editor's reveal/scroll lands
// on it — the two derived read-backs included: the locked input carries the key
// exactly as the editable branch's input does, which is also what lets the reveal
// move focus there rather than to an unfocusable wrapper. With more than one
// mount on the page that key alone is no longer unique, so the id it renders is
// namespaced per mount (useFieldDomId / fieldDomId.ts) while the ERROR keys stay
// bare — the panel is handed errors already stripped back to the bare key.

import { useTranslation } from 'react-i18next';

import {
  ASSEMBLY_BOM_FIELD_KEY,
  ASSEMBLY_MODES,
  ASSEMBLY_MODE_FIELD_KEY,
  ASSEMBLY_MODE_LABELS,
  assemblyMode,
  assemblyModeSwitchClearsRows,
  BOX_FINISH_LABELS,
  CLOSURE_METHOD_LABELS,
  FILLER_COLOR_LABELS,
  FILLER_TYPE_LABELS,
  LABEL_PLACEMENT_LABELS,
  LABEL_PURPOSE_LABELS,
  OVERBOXING_LABELS,
  SPECIAL_ACTION_LABELS,
  acceptAttrForRole,
  bomSnapshotFromCompanyItem,
  deriveKitComponentsCount,
  deriveKitComponentsSupplied,
  isPartnerStock,
  KIT_COMPONENT_SOURCES,
  KIT_COMPONENT_SOURCE_LABELS,
  KIT_TYPES,
  KIT_TYPE_LABELS,
  kitContentRowsFromBomSnapshot,
  REWORK_ACTIONS,
  REWORK_ACTIONS_FIELD_KEY,
  REWORK_ACTION_LABELS,
  type AssemblyBuildStep,
  type AssemblyMode,
  type AssemblySnapshot,
  type CompanyItem,
  type KitContentRow,
  type ReworkAction,
} from 'shared';

import { formatNumber } from '../i18n/formatters.js';
import { parseWholeNumberInput, WHOLE_NUMBER_INPUT_PROPS } from '../lib/wholeNumberInput.js';
import { SUPPORT_EMAIL } from '../pricingCopy.js';
import { BuildStepsEditor } from './BuildStepsEditor.js';
import { CompanyItemPicker } from './CompanyItemPicker.js';
import { useFieldDomId } from './fieldDomId.js';
import { FieldReadBack, readBackValue } from './FieldReadBack.js';
import { KitContentsEditor, newKitContentRowId, type KitContentLineOption } from './KitContentsEditor.js';
import { PhotoStrip } from './PhotoStrip.js';
import { PASTE_HINT_SUPPORTED, PASTE_SHORTCUT } from './usePasteFiles.js';
import { KIT_PHOTO_ROLE, type AssemblyPhotos } from './useAssemblyPhotos.js';
import { useCompanyStock } from './useCompanyStock.js';
import { localizeVocabLabels } from './vocabLabels.js';

const KIT_PHOTO_ACCEPT_ATTR = acceptAttrForRole(KIT_PHOTO_ROLE);

// A labeled radio group whose fieldset carries the field-key id, so the
// editor's reveal/scroll lands on it (mirrors ShipToForm's delivery_method
// fieldset).
//
// `disabled` renders the ANSWER as static text instead of a dead picker. A
// natively disabled radio is out of the tab order, so a disabled group is a set
// of options no keyboard or screen reader can reach — and the one that was picked
// becomes unreadable along with the seven that were not. The only surface that
// passes `disabled` today is an assembly item's panel on a submitted order,
// which asks nothing; the same reasoning AssemblyControl already gives for
// reading the pre-made-labels flag back as a sentence there.
export function EnumRadios<T extends string>(props: {
  fieldKey: string;
  legend: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: T | undefined;
  onPick: (v: T) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  // Defaults to required (asterisk + no "(optional)"). Optional groups omit the
  // marker. Either way the fieldset wires aria-invalid + the error span so a
  // backend-keyed failure on this field always renders inline.
  required?: boolean;
  help?: string;
}): JSX.Element {
  const {
    fieldKey,
    legend,
    options,
    labels,
    value,
    onPick,
    errors,
    disabled,
    required = true,
    help,
  } = props;
  const domId = useFieldDomId(fieldKey);
  const err = errors[fieldKey];
  return (
    <fieldset
      id={domId}
      className={`field-group${err ? ' field-group--error' : ''}`}
      aria-invalid={err ? true : undefined}
      aria-describedby={err ? `${domId}-error` : undefined}
    >
      <legend className="field-group__legend">
        {legend}
        {/* No marker on a read-back: it would promise a gate that has nothing
            left to report. */}
        {required && !disabled && <span className="required-marker"> *</span>}
      </legend>
      {/* The help asks the customer to pick something; a read-back is not asking. */}
      {help && !disabled && <span className="help help--block">{help}</span>}
      {disabled ? (
        <p className="value--locked">{readBackValue(value ? labels[value] : undefined)}</p>
      ) : (
        options.map((o) => (
          <label key={o} className="field-group__option">
            <input
              type="radio"
              name={domId}
              value={o}
              checked={value === o}
              onChange={() => onPick(o)}
            />
            <span>{labels[o]}</span>
          </label>
        ))
      )}
      {err && (
        <span className="field-error" id={`${domId}-error`}>
          {err}
        </span>
      )}
    </fieldset>
  );
}

// A labeled multi-select: the same fieldset-carries-the-field-key contract
// EnumRadios keeps, over a set of independent checkboxes rather than one pick.
// The picked set is rebuilt from the canonical key order on every change, so the
// stored array reads in the order the options are offered no matter which box
// the customer ticked first — which is also the order the read-back below reads
// them in. `disabled` renders that read-back instead of the checkboxes, for the
// reason EnumRadios gives above.
export function EnumChecks<T extends string>(props: {
  fieldKey: string;
  legend: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: readonly T[] | undefined;
  onChange: (v: T[]) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  required?: boolean;
  help?: string;
}): JSX.Element {
  const {
    fieldKey,
    legend,
    options,
    labels,
    value,
    onChange,
    errors,
    disabled,
    required = true,
    help,
  } = props;
  const domId = useFieldDomId(fieldKey);
  const err = errors[fieldKey];
  const picked = new Set(value ?? []);
  function toggle(option: T, on: boolean): void {
    const next = new Set(picked);
    if (on) next.add(option);
    else next.delete(option);
    onChange(options.filter((o) => next.has(o)));
  }
  return (
    <fieldset
      id={domId}
      className={`field-group${err ? ' field-group--error' : ''}`}
      aria-invalid={err ? true : undefined}
      aria-describedby={err ? `${domId}-error` : undefined}
    >
      <legend className="field-group__legend">
        {legend}
        {required && !disabled && <span className="required-marker"> *</span>}
      </legend>
      {help && !disabled && <span className="help help--block">{help}</span>}
      {disabled ? (
        <p className="value--locked">
          {readBackValue(
            options
              .filter((o) => picked.has(o))
              .map((o) => labels[o])
              .join(', '),
          )}
        </p>
      ) : (
        options.map((o) => (
          <label key={o} className="field-group__option">
            <input
              type="checkbox"
              name={domId}
              value={o}
              checked={picked.has(o)}
              onChange={(e) => toggle(o, e.target.checked)}
            />
            <span>{labels[o]}</span>
          </label>
        ))
      )}
      {err && (
        <span className="field-error" id={`${domId}-error`}>
          {err}
        </span>
      )}
    </fieldset>
  );
}

// A labeled single-line free-text field carrying the field-key id on the input —
// NumberField's sibling, for the one describe-it box this file renders itself
// (the rework list's "Something else"). The eight packing-axis boxes are seeded
// FormSchema fields and SchemaForm draws those; this exists because the rework
// actions are a hand-written control, so their box has to be too.
//
// `disabled` reads the text back as locked TEXT for the reason NumberField gives:
// a natively disabled input is out of the tab order, so on a submitted order the
// customer's own answer would be unreachable by keyboard and screen reader.
function TextField(props: {
  fieldKey: string;
  label: string;
  help?: string;
  placeholder?: string;
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}): JSX.Element {
  const { fieldKey, label, help, placeholder, value, onChange, errors, disabled } = props;
  const domId = useFieldDomId(fieldKey);
  const err = errors[fieldKey];
  return (
    <div className={`form-row${err ? ' form-row--error' : ''}`}>
      <label htmlFor={domId}>{label}</label>
      {help && !disabled && <span className="help help--block">{help}</span>}
      <input
        id={domId}
        type="text"
        {...(disabled
          ? { readOnly: true, 'aria-readonly': true, className: 'input--locked' }
          : placeholder !== undefined
            ? { placeholder }
            : {})}
        value={disabled ? readBackValue(value) : (value ?? '')}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        aria-invalid={err ? true : undefined}
        aria-describedby={err ? `${domId}-error` : undefined}
      />
      {err && (
        <span className="field-error" id={`${domId}-error`}>
          {err}
        </span>
      )}
    </div>
  );
}

// A labeled positive-integer field carrying the field-key id on the input.
// Required by default (asterisk); pass required={false} for optional fields so
// the marker matches what the submit gate actually enforces.
//
// `disabled` reads the number back as locked TEXT, the way the two derived counts
// below already do: a readOnly number input still draws its spinners in some
// browsers — a nudge control that does nothing — and the thousands separator is
// how the customer's own answer reads everywhere else on the page. The required
// marker goes with the control, because it promises a gate with nothing left to
// report; the help STAYS, because the only one passed here explains a consequence
// (kits × per-kit quantity is what set a project's print run) rather than asking
// for anything, and that reading is exactly what a submitted order needs.
export function NumberField(props: {
  fieldKey: string;
  label: string;
  help?: string;
  // An example value, shown IN the input rather than as a sentence beside it —
  // "e.g. 40 per box" earns no help paragraph of its own.
  placeholder?: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  required?: boolean;
  // Caps the input width for small counts (e.g. components per kit) so a
  // two-digit value doesn't sit in a full-width field.
  compact?: boolean;
}): JSX.Element {
  const {
    fieldKey,
    label,
    help,
    placeholder,
    value,
    onChange,
    errors,
    disabled,
    required = true,
    compact,
  } = props;
  const domId = useFieldDomId(fieldKey);
  const err = errors[fieldKey];
  return (
    <div className={`form-row${err ? ' form-row--error' : ''}`}>
      <label htmlFor={domId}>
        {label}
        {required && !disabled && <span className="required-marker"> *</span>}
      </label>
      {help && <span className="help help--block">{help}</span>}
      <input
        id={domId}
        {...(disabled
          ? {
              type: 'text',
              readOnly: true,
              'aria-readonly': true,
              className: `${compact ? 'input-compact ' : ''}input--locked`,
              value: readBackValue(typeof value === 'number' ? formatNumber(value) : undefined),
            }
          : {
              ...WHOLE_NUMBER_INPUT_PROPS,
              ...(compact ? { className: 'input-compact' } : {}),
              ...(placeholder !== undefined ? { placeholder } : {}),
              value: typeof value === 'number' ? value : '',
            })}
        onChange={(e) => {
          const n = parseWholeNumberInput(e.target.value);
          if (n !== undefined) onChange(n === null || n < 1 ? undefined : n);
        }}
        aria-invalid={err ? true : undefined}
        aria-describedby={err ? `${domId}-error` : undefined}
      />
      {err && (
        <span className="field-error" id={`${domId}-error`}>
          {err}
        </span>
      )}
    </div>
  );
}

export type AssemblyRecapContent = {
  isRework: boolean;
  // The "N components per kit" headline, or null when there is none to state.
  componentsPerKit: number | null;
  rows: { label: string; value: string }[];
};

// Plain-English readback of the kitting choices — ONE builder, read by the
// SpecSummary rail beside an `assembly` item's configurator (and the order's
// review strip, which renders that same rail per item). Reads
// back captured facts plus the TWO derived values the snapshot itself carries —
// the component count and who supplies the components — through the same
// deriveKitComponentsCount / deriveKitComponentsSupplied that
// normalizeOrderServices persists from, so no summary can state something the
// saved snapshot would not. Display only; nothing here is priced.
export function useAssemblyRecap(assembly: AssemblySnapshot): AssemblyRecapContent {
  const { t } = useTranslation(['quote', 'common']);
  // The same catalog keys the form renders these picks from, so the recap and
  // the section it summarizes can never speak different words for one pick —
  // the rule the axis read-back below already follows.
  const reworkActionLabels = localizeVocabLabels<ReworkAction>(
    'orderServices.assembly.rework.actions',
    REWORK_ACTION_LABELS,
    t
  );
  const kitTypeLabels = localizeVocabLabels(
    'orderServices.assembly.kitTypeOptions',
    KIT_TYPE_LABELS,
    t
  );
  const kitComponentSourceLabels = localizeVocabLabels(
    'orderServices.assembly.componentsSuppliedOptions',
    KIT_COMPONENT_SOURCE_LABELS,
    t
  );
  const rows: { label: string; value: string }[] = [];
  // A rework reads back what happens to the stock. The kit-shaped rows are not
  // merely unset there — a customer who filled them in and then switched mode
  // still HAS them stored (nothing captured is dropped), so the recap has to
  // pick by mode rather than by whether a value happens to be present, or it
  // would read back packaging for a job that packages nothing.
  const isRework = assemblyMode(assembly) === 'rework';
  const isKitBuild = assemblyMode(assembly) === 'build_kit';
  const stockRows = assembly.kit_contents?.length ?? 0;
  if (!isRework && !isKitBuild && stockRows > 0) {
    rows.push({
      label: t('orderServices.recap.rowStockItems'),
      value: formatNumber(stockRows),
    });
  }
  if (isRework) {
    const actions = assembly.rework_actions ?? [];
    if (actions.length > 0) {
      rows.push({
        label: t('orderServices.recap.rowRework'),
        // "Something else" reads back as what the customer said it was, the same
        // substitution the packing axes make below.
        value: actions
          .map((a) =>
            a === 'other' && assembly.rework_actions_other
              ? assembly.rework_actions_other
              : reworkActionLabels[a],
          )
          .join(', '),
      });
    }
    if (stockRows > 0) {
      rows.push({
        label: t('orderServices.recap.rowStockItems'),
        value: formatNumber(stockRows),
      });
    }
  }
  // Shown in BOTH modes, unlike the kit-shaped rows below it. In a kit build it
  // is an optional shortcut; in a rework it is the FIRST and blocking question,
  // so a recap that hid it would leave out the one thing the job is about.
  const bomName = assembly.bom_snapshot?.name ?? assembly.bom_id;
  if (bomName) {
    rows.push({
      label: isRework ? t('orderServices.recap.rowReworkBom') : t('orderServices.recap.rowBom'),
      value: bomName,
    });
  }
  if (isKitBuild && assembly.kit_type) {
    rows.push({ label: t('orderServices.recap.rowPackaging'), value: kitTypeLabels[assembly.kit_type] });
  }
  // Derived, exactly as the count below is: every contents row names its own
  // supply source, so reading the raw quote-level field would let the rail state
  // one thing while the form (and the saved snapshot) said another.
  const suppliedSummary = deriveKitComponentsSupplied(assembly);
  if (isKitBuild && suppliedSummary) {
    rows.push({
      label: t('orderServices.recap.rowComponents'),
      value: kitComponentSourceLabels[suppliedSummary],
    });
  }
  if (typeof assembly.units_per_pack === 'number' && assembly.units_per_pack > 0) {
    rows.push({
      label: t('orderServices.recap.rowPerPack'),
      value: t('orderServices.recap.perPackUnits', { count: assembly.units_per_pack }),
    });
  }

  // The packing-detail axes read back through the SAME catalog keys the form
  // renders them from (schemaField.<key>.option.<value>), with the canonical
  // vocabulary label as the fallback — so the recap and the section it summarizes
  // can never speak different words for one pick.
  //
  // Unlike the kit-shaped rows above, these are PRESENCE-gated rather than
  // mode-gated. The invariant that makes that correct lives elsewhere:
  // scrubConfigForVisibility (in AssemblyControl.writeAssembly) clears an axis
  // whose gate stopped holding, so a present value is by construction a value the
  // form still asks for. A change to PACKING_APPLIES has to preserve it.
  const axisLabel = (fieldKey: string, value: string, fallback: string): string =>
    t(`schemaField.${fieldKey}.option.${value}`, { defaultValue: fallback });

  // An `other` pick reads back in the customer's OWN words once they have filled
  // the axis's describe-it box, and as the option's label until they do — so the
  // recap says "shredded kraft paper" where the form says "Something else", which
  // is the only version of that row a rep can act on. Presence-gated like the
  // rest: a box whose pick was dropped was scrubbed with it.
  const axisText = (
    fieldKey: string,
    value: string,
    fallback: string,
    spelledOut: string | undefined,
  ): string => (value === 'other' && spelledOut ? spelledOut : axisLabel(fieldKey, value, fallback));

  const fillers = (assembly.filler_type ?? []).map((v) =>
    axisText('filler_type', v, FILLER_TYPE_LABELS[v], assembly.filler_type_other),
  );
  const fillerColors = (assembly.filler_color ?? []).map((v) =>
    axisText('filler_color', v, FILLER_COLOR_LABELS[v], assembly.filler_color_other),
  );
  if (fillers.length > 0) {
    rows.push({
      label: t('orderServices.recap.rowFiller'),
      value:
        fillerColors.length > 0
          ? `${fillers.join(', ')} (${fillerColors.join(', ')})`
          : fillers.join(', '),
    });
  }
  if (assembly.closure_method) {
    rows.push({
      label: t('orderServices.recap.rowClosure'),
      value: axisText(
        'closure_method',
        assembly.closure_method,
        CLOSURE_METHOD_LABELS[assembly.closure_method],
        assembly.closure_method_other,
      ),
    });
  }
  if (assembly.box_finish) {
    rows.push({
      label: t('orderServices.recap.rowBoxFinish'),
      value: axisText(
        'box_finish',
        assembly.box_finish,
        BOX_FINISH_LABELS[assembly.box_finish],
        assembly.box_finish_other,
      ),
    });
  }
  if (assembly.overboxing) {
    rows.push({
      label: t('orderServices.recap.rowOverboxing'),
      value: axisText(
        'overboxing',
        assembly.overboxing,
        OVERBOXING_LABELS[assembly.overboxing],
        assembly.overboxing_other,
      ),
    });
  }
  // The label note answers the serial / lot / date code pick as well as `other`:
  // it reads in place of "Something else", and after the code pick.
  const labelNote = assembly.label_purpose_other;
  const labelPurposes = (assembly.label_purpose ?? []).map((v) => {
    const text = axisText('label_purpose', v, LABEL_PURPOSE_LABELS[v], labelNote);
    return v === 'serial_or_lot' && labelNote ? `${text} (${labelNote})` : text;
  });
  if (labelPurposes.length > 0) {
    const placement = assembly.label_placement
      ? axisText(
          'label_placement',
          assembly.label_placement,
          LABEL_PLACEMENT_LABELS[assembly.label_placement],
          assembly.label_placement_other,
        )
      : null;
    rows.push({
      label: t('orderServices.recap.rowLabels'),
      value: placement ? `${labelPurposes.join(', ')} — ${placement}` : labelPurposes.join(', '),
    });
  }
  const specialActions = (assembly.special_actions ?? []).map((v) =>
    axisText('special_actions', v, SPECIAL_ACTION_LABELS[v], assembly.special_actions_other),
  );
  if (specialActions.length > 0) {
    rows.push({ label: t('orderServices.recap.rowAlso'), value: specialActions.join(', ') });
  }

  const stepCount = assembly.build_steps?.length ?? 0;
  if (stepCount > 0) {
    rows.push({
      label: t('orderServices.recap.rowBuildSteps'),
      value: t('orderServices.recap.buildStepCount', { count: stepCount }),
    });
  }

  // Read back in BOTH modes whenever it is set. A rework is not ASKED this, but a
  // value carried in from a kit build still crosses to Nexus — and the recap's job
  // is to show what we are sending, not what the current mode would have asked.
  if (assembly.purchased_labels === true) {
    rows.push({
      label: t('orderServices.recap.preMadeLabelsFlag'),
      value: t('orderServices.recap.preMadeLabelsValue'),
    });
  }

  const count = deriveKitComponentsCount(assembly);
  const componentsPerKit = isKitBuild && typeof count === 'number' && count > 0 ? count : null;
  return { isRework, componentsPerKit, rows };
}

type KittingFieldsProps = {
  assembly: AssemblySnapshot;
  onPatchAssembly: (patch: Partial<AssemblySnapshot>) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  // The quote's PERSISTED projects, offered as the `quote_line` origin on a
  // contents row.
  lines?: readonly KitContentLineOption[];
  hasUnsavedLines?: boolean;
  // Forwarded to the contents list — see its own prop.
  onRequestSave?: (() => void) | undefined;
  // The quote's reference photos — the finished-kit shot and the per-step ones.
  // They live in Asset rows rather than on the snapshot, so they are fetched
  // once for the whole section and handed down (useAssemblyPhotos), not
  // re-fetched per slot.
  photos: AssemblyPhotos;
  // The level the two lists' titles render at, threaded straight through to
  // them so this section and the packing-detail axes beside it sit at ONE depth.
  // See AssemblyPanel's own prop for who decides it.
  headingLevel?: 'h4' | 'h5';
};

// The kit-or-SKU picker inside fetches the company's stock itself, so either
// call site can drop this in with no query plumbing of its own. An empty list (no matched Company, no kit BOMs, or a degraded Nexus call) still
// renders the picker, and the picker says it has nothing on file — a hidden
// picker reads as a missing feature, not as an empty catalog.
// Non-price company data, so caching is permitted (CLAUDE.md §Caching).
export function KittingFields({
  assembly,
  onPatchAssembly,
  errors,
  disabled,
  lines = [],
  hasUnsavedLines = false,
  onRequestSave,
  photos,
  headingLevel = 'h5',
}: KittingFieldsProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  // Whether this Company has stock of its own on file — which decides only whether a
  // rework's dead end is spelled out beside the picker. The picker renders either
  // way. Not the picker's list; see useCompanyStock.
  const hasOwnStock = useCompanyStock({ enabled: !disabled });

  // Attach (or clear) a BOM to the kit. Picking one stores its WMS SKU (bom_id)
  // plus a display snapshot; "No BOM" clears both so the customer can describe
  // the kit by hand. bom_id is the key Nexus echoes to ArgoWMS; the snapshot
  // keeps the recap readable if the catalog later changes.
  // Once the customer has authored contents rows the row count IS the answer, so
  // the number is READ-ONLY there: normalizeOrderServices persists exactly this
  // derivation on save, and an editable input would let a typed number be
  // silently overwritten by it. With no rows the entered number still stands and
  // the field stays editable — a kit authored before the contents list existed
  // keeps reading and validating unchanged.
  const rows: readonly KitContentRow[] = assembly.kit_contents ?? [];
  const steps: readonly AssemblyBuildStep[] = assembly.build_steps ?? [];
  const hasRows = rows.length > 0;
  const derivedCount = deriveKitComponentsCount(assembly);
  const countError = errors['assembly.kit_components_count'];
  const countDomId = useFieldDomId('assembly.kit_components_count');
  // Every stored row names its own supply source, so once rows exist the
  // quote-level question is answered by them and asking it again asks twice.
  const derivedSupplied = deriveKitComponentsSupplied(assembly);
  const suppliedError = errors['assembly.kit_components_supplied'];
  const suppliedDomId = useFieldDomId('assembly.kit_components_supplied');
  const kitPhotoDomId = useFieldDomId('assembly.kit_photo');

  // Build a kit, or rework stock already held (ADR-018 Decision 7). The mode
  // switches the voice of the two lists and drops the kit-shaped questions a
  // rework has no answer for — the SAME conditionality the submit gate applies,
  // read from the same stored field through the same resolver, so the form never
  // asks for something the gate ignores or hides something it requires.
  const mode: AssemblyMode = assemblyMode(assembly);
  const isRework = mode === 'rework';
  const isKitBuild = mode === 'build_kit';
  // Enum labels come from the canonical vocabulary, translated by value with that
  // string as the fallback — the shape a schema-served label takes, so a locale
  // that has not caught up reads English rather than a raw key. See
  // localizeVocabLabels: walking the map is what stops a new option shipping
  // untranslated.
  const modeLabels = localizeVocabLabels<AssemblyMode>(
    'orderServices.assembly.mode.options',
    ASSEMBLY_MODE_LABELS,
    t
  );
  const reworkActionLabels = localizeVocabLabels<ReworkAction>(
    'orderServices.assembly.rework.actions',
    REWORK_ACTION_LABELS,
    t
  );
  const kitComponentSourceLabels = localizeVocabLabels(
    'orderServices.assembly.componentsSuppliedOptions',
    KIT_COMPONENT_SOURCE_LABELS,
    t
  );
  const kitTypeLabels = localizeVocabLabels(
    'orderServices.assembly.kitTypeOptions',
    KIT_TYPE_LABELS,
    t
  );

  // Picking the kit is what a rework is FOR, so the pick also fills the item list
  // in ("once they select the kit, everything fills in" — the 2026-08-20 meeting).
  //
  // It runs HERE, on the pick, and never as a derivation of bom_snapshot during
  // render: a read-time version would re-impose the catalog's rows over every
  // edit the customer made, which is the failure R26 names. What lands is
  // ordinary authored rows — editable, removable, reorderable — because the whole
  // point of a rework is that the kit changes.
  //
  // It only writes when the list is EMPTY or still holds exactly the LAST pick's
  // untouched pre-fill. Once the customer has edited the list it is theirs, and a
  // later pick leaves it alone rather than silently discarding their work.
  // Is the item list still exactly what the LAST kit pick put there? Compared on
  // the fields a pre-filled row carries, not on row_id (freshly minted every
  // time). An empty list counts: there is nothing to lose.
  //
  // This is a UI freshness test — "may I overwrite this?" — not a completeness
  // rule, so it belongs here rather than beside assemblyMissingFieldKeys (R12
  // governs what the gate decides, and this decides nothing the gate reads).
  const rowsAreUntouchedPrefill = ((): boolean => {
    if (rows.length === 0) return true;
    const fromLastPick = kitContentRowsFromBomSnapshot(assembly.bom_snapshot, () => '');
    if (fromLastPick.length !== rows.length) return false;
    return fromLastPick.every((expected, i) => {
      const actual = rows[i];
      return (
        !!actual &&
        actual.origin === expected.origin &&
        actual.role === expected.role &&
        actual.supply_source === expected.supply_source &&
        actual.description === expected.description &&
        actual.sku === expected.sku &&
        actual.quantity_per_kit === expected.quantity_per_kit &&
        actual.unit === expected.unit
      );
    });
  })();

  const bomError = errors[ASSEMBLY_BOM_FIELD_KEY];
  // ONE picker, offered FIRST in both modes, because in both it is the question
  // the rest of the section elaborates: is this a kit we already hold for you?
  // In rework mode it also BLOCKS and PRE-FILLS the item list — the SKU "drives
  // everything", and a rework of stock nobody has named is not a job we can take.
  // In a kit build it stays optional, is offered only when there is something to
  // offer, and stores the pick and its snapshot WITHOUT pre-filling rows: a build
  // is authored from scratch, and the saved kit is what the recap names. Same
  // field, same stored value, same transmit path (ADR-018) — the gate and the
  // pre-fill are what differ by mode.
  const bomLabel = isRework
    ? t('orderServices.assembly.rework.bomLabel')
    : t('orderServices.assembly.bomLabel');
  // The error stays keyed by the BARE field key — the panel is handed errors that
  // way — while the id it renders is namespaced for the mount (fieldDomId).
  const bomDomId = useFieldDomId(ASSEMBLY_BOM_FIELD_KEY);
  // What the customer picked, by the name frozen at pick time — the same value the
  // recap reads, and for the same reason.
  const pickedBomName = assembly.bom_snapshot?.name ?? assembly.bom_id;
  // The read-back a submitted order gets instead of the picker. It is not merely
  // the picker locked: the item-master query is switched off when nothing on the
  // order can change, so the picker would have nothing but the frozen label to go
  // on. The three help lines go with it, because each one asks for an action
  // (start here, clear the rows, email us) that this surface cannot take.
  const bomReadback = (
    <FieldReadBack id={bomDomId} label={bomLabel} value={pickedBomName} />
  );
  const bomPicker = (
    <div className={`form-row${bomError ? ' form-row--error' : ''}`}>
      <label htmlFor={bomDomId}>
        {bomLabel}
        {isRework && <span className="required-marker"> *</span>}
      </label>
      {isRework && (
        <span className="help help--block">{t('orderServices.assembly.rework.bomHelp')}</span>
      )}
      {/* The SAME control, reading the SAME feed, as the per-row picker in the
          item list below — one question about a Company's stock. `storedLabel` is
          the name frozen at pick time, the last thing left to name a pick the
          catalog no longer surfaces. */}
      <CompanyItemPicker
        id={bomDomId}
        value={assembly.bom_id ?? ''}
        storedLabel={pickedBomName ?? undefined}
        // Mode-specific on purpose. "No saved kit, I'll describe it" is TRUE
        // where the pick is optional and FALSE where it blocks — reusing it in
        // rework mode would offer an out the gate then refuses.
        emptyOptionLabel={
          isRework
            ? t('orderServices.assembly.rework.bomEmptyOption')
            : t('orderServices.assembly.bomEmptyOption')
        }
        // "Which of YOUR kits" — the partner's own stock, never Argo's packaging.
        offers={isPartnerStock}
        onPick={pickBom}
        disabled={disabled}
        invalid={!!bomError}
        {...(bomError ? { describedBy: `${bomDomId}-error` } : {})}
      />
      {/* A rework needs a kit to point at, so an empty catalog is a dead end the
          customer cannot work their way out of — say so up front, not only inside
          the closed dropdown, and give them the way out we give every other dead
          end. Deliberately not phrased as a fact about Argo's records: the same
          empty list is what a Nexus outage produces. */}
      {isRework && !hasOwnStock && (
        <span className="help help--block">
          {t('orderServices.assembly.rework.bomNoneOnFile', { email: SUPPORT_EMAIL })}
        </span>
      )}
      {isRework && !rowsAreUntouchedPrefill && assembly.bom_id && (
        <span className="help help--block">
          {t('orderServices.assembly.rework.bomListKeptNote')}
        </span>
      )}
      {bomError && (
        <span className="field-error" id={`${bomDomId}-error`}>
          {bomError}
        </span>
      )}
    </div>
  );

  // The picker hands back the ITEM, so the snapshot is built from the row the
  // customer actually saw — no second lookup, and nothing to keep in sync with the
  // list the pick came from.
  //
  // A pick with no components pre-fills nothing and that is correct: a plain
  // stocked SKU has no component tree, and the customer lists the stock to work on
  // in the item list below. Only a KIT carries rows to seed from.
  function pickBom(item: CompanyItem | null): void {
    if (!item) {
      // Clearing the pick leaves the rows: they are the customer's list now,
      // whatever put them there.
      onPatchAssembly({ bom_id: undefined, bom_snapshot: undefined });
      return;
    }
    const patch: Partial<AssemblySnapshot> = {
      bom_id: item.item_id,
      bom_snapshot: bomSnapshotFromCompanyItem(item),
    };
    if (isRework && rowsAreUntouchedPrefill) {
      const prefilled = kitContentRowsFromBomSnapshot(patch.bom_snapshot, newKitContentRowId);
      if (prefilled.length > 0) patch.kit_contents = prefilled;
    }
    onPatchAssembly(patch);
  }

  function pickMode(next: AssemblyMode): void {
    if (next === mode) return;
    if (!assemblyModeSwitchClearsRows(mode, next, rows)) {
      onPatchAssembly({ assembly_mode: next });
      return;
    }
    if (!confirm(t('orderServices.assembly.mode.confirmClearRows', { count: rows.length }))) return;
    onPatchAssembly({ assembly_mode: next, kit_contents: [] });
  }

  return (
    <>
      <EnumRadios
        fieldKey={ASSEMBLY_MODE_FIELD_KEY}
        legend={t('orderServices.assembly.mode.legend')}
        help={t('orderServices.assembly.mode.help')}
        options={ASSEMBLY_MODES}
        labels={modeLabels}
        value={mode}
        onPick={pickMode}
        errors={errors}
        disabled={disabled}
        // No asterisk: one option is always selected, because an unset mode
        // resolves to the kit build every stored snapshot already is. The
        // marker would promise a gate that has nothing to report.
        required={false}
      />
      {isKitBuild || isRework || assembly.bom_id
        ? disabled
          ? pickedBomName && bomReadback
          : bomPicker
        : null}
      <PhotoStrip
        inputId={kitPhotoDomId}
        accept={KIT_PHOTO_ACCEPT_ATTR}
        labels={{
          label: !isKitBuild
            ? t('orderServices.assembly.kitPhoto.reworkLabel')
            : t('orderServices.assembly.kitPhoto.label'),
          optionalSuffix: t('shipTo.fields.optionalSuffix'),
          help: !isKitBuild
            ? t('orderServices.assembly.kitPhoto.reworkHelp')
            : t('orderServices.assembly.kitPhoto.help'),
          ...(PASTE_HINT_SUPPORTED
            ? { pasteHint: t('common:paste.imageHint', { shortcut: PASTE_SHORTCUT }) }
            : {}),
          loading: t('orderServices.assembly.photos.loading'),
          dismiss: t('orderServices.assembly.photos.dismiss'),
          remove: (filename) => t('orderServices.assembly.photos.remove', { filename }),
          uploadProgress: (filename) =>
            t('orderServices.assembly.photos.uploadProgress', { filename }),
          takePhoto: t('orderServices.assembly.photos.takePhoto'),
        }}
        assets={photos.kitPhotos}
        pending={photos.pending.filter((p) => p.role === KIT_PHOTO_ROLE)}
        loading={photos.loading}
        error={photos.error?.role === KIT_PHOTO_ROLE ? photos.error.message : null}
        onPick={photos.uploadKit}
        onRemove={(asset) => void photos.remove(asset)}
        onDismiss={photos.dismissPending}
        disabled={disabled || photos.locked}
      />
      {/* Locked because the line has no id yet, not because the order is closed —
          so it says what to do instead of reading back an empty answer. */}
      {photos.awaitingLine && !disabled && (
        <p className="help help--flush">{t('orderServices.assembly.photos.awaitingLineNote')}</p>
      )}
      {isRework && (
        <EnumChecks
          fieldKey={REWORK_ACTIONS_FIELD_KEY}
          legend={t('orderServices.assembly.rework.actionsLegend')}
          help={t('orderServices.assembly.rework.actionsHelp')}
          options={REWORK_ACTIONS}
          labels={reworkActionLabels}
          value={assembly.rework_actions}
          // Untick "Something else" and its box goes with it, in the SAME patch
          // that drops the pick. The schema's own scrub cannot do it here: these
          // actions are a hand-written control, so neither the list nor its box is
          // a field scrubConfigForVisibility knows about — the eight packing axes
          // get that for free from their conditional_show.
          onChange={(v) =>
            onPatchAssembly(
              v.includes('other')
                ? { rework_actions: v }
                : { rework_actions: v, rework_actions_other: undefined },
            )
          }
          errors={errors}
          disabled={disabled}
        />
      )}
      {/* The answer to that "Something else", typed where it was picked. Rendered
          only when the pick stands — and on a submitted order only when there is
          text to read back, the same rule the pre-made-labels flag follows. */}
      {isRework &&
        (assembly.rework_actions ?? []).includes('other') &&
        (!disabled || !!assembly.rework_actions_other) && (
          <TextField
            fieldKey="assembly.rework_actions_other"
            label={t('orderServices.assembly.rework.actionsOtherLabel')}
            placeholder={t('orderServices.assembly.rework.actionsOtherPlaceholder')}
            value={assembly.rework_actions_other}
            onChange={(v) => onPatchAssembly({ rework_actions_other: v })}
            errors={errors}
            disabled={disabled}
          />
        )}
      <KitContentsEditor
        rows={rows}
        onChange={(next) => onPatchAssembly({ kit_contents: next })}
        lines={lines}
        hasUnsavedLines={hasUnsavedLines}
        onRequestSave={onRequestSave}
        errors={errors}
        disabled={disabled}
        mode={mode}
        headingLevel={headingLevel}
      />
      {/* The contents list is followed immediately by the two values derived FROM
          it: "5 components per kit", and who supplies them. How many kits is the
          item's own Quantity field, one section up. */}
      {isKitBuild &&
        (hasRows ? (
          <div className={`form-row${countError ? ' form-row--error' : ''}`}>
            <label htmlFor={countDomId}>
              {t('orderServices.assembly.componentsCountLabel')}
            </label>
            {/* No required marker on the derived read-back: the gate still
                requires the count, but the derivation already satisfies it, so a
                marker here would promise a field the customer cannot reach. The
                editable branch below keeps its marker — there the value is
                theirs to supply. */}
            <span className="help help--block" id={`${countDomId}-help`}>
              {t('orderServices.assembly.componentsCountDerivedHelp')}
            </span>
            <input
              id={countDomId}
              type="text"
              readOnly
              aria-readonly="true"
              className="input-compact input--locked"
              value={readBackValue(
                typeof derivedCount === 'number' ? formatNumber(derivedCount) : undefined
              )}
              aria-invalid={countError ? true : undefined}
              aria-describedby={[
                `${countDomId}-help`,
                countError ? `${countDomId}-error` : null,
              ]
                .filter(Boolean)
                .join(' ')}
            />
            {countError && (
              <span className="field-error" id={`${countDomId}-error`}>
                {countError}
              </span>
            )}
          </div>
        ) : (
          <NumberField
            fieldKey="assembly.kit_components_count"
            label={t('orderServices.assembly.componentsCountLabel')}
            value={assembly.kit_components_count}
            onChange={(v) => onPatchAssembly({ kit_components_count: v })}
            errors={errors}
            disabled={disabled}
            compact
          />
        ))}
      {isKitBuild &&
        (derivedSupplied && hasRows ? (
          <div className={`form-row${suppliedError ? ' form-row--error' : ''}`}>
            <label htmlFor={suppliedDomId}>
              {t('orderServices.assembly.componentsSuppliedLegend')}
            </label>
            <span className="help help--block" id={`${suppliedDomId}-help`}>
              {t('orderServices.assembly.componentsSuppliedDerivedHelp')}
            </span>
            <input
              id={suppliedDomId}
              type="text"
              readOnly
              aria-readonly="true"
              className="input--locked"
              value={kitComponentSourceLabels[derivedSupplied]}
              aria-invalid={suppliedError ? true : undefined}
              aria-describedby={[
                `${suppliedDomId}-help`,
                suppliedError ? `${suppliedDomId}-error` : null,
              ]
                .filter(Boolean)
                .join(' ')}
            />
            {suppliedError && (
              <span className="field-error" id={`${suppliedDomId}-error`}>
                {suppliedError}
              </span>
            )}
          </div>
        ) : (
          <EnumRadios
            fieldKey="assembly.kit_components_supplied"
            legend={t('orderServices.assembly.componentsSuppliedLegend')}
            options={KIT_COMPONENT_SOURCES}
            labels={kitComponentSourceLabels}
            value={assembly.kit_components_supplied}
            onPick={(v) => onPatchAssembly({ kit_components_supplied: v })}
            errors={errors}
            disabled={disabled}
          />
        ))}
      <BuildStepsEditor
        steps={steps}
        onChange={(next) => onPatchAssembly({ build_steps: next })}
        photos={photos}
        errors={errors}
        disabled={disabled}
        mode={mode}
        headingLevel={headingLevel}
      />
      {/* Packaging closes the section: kit_type gates box_finish and shares its
          subject with the packing-detail axes, which AssemblyDetailFields renders
          immediately after this field set. */}
      {isKitBuild && (
        <EnumRadios
          fieldKey="assembly.kit_type"
          legend={t('orderServices.assembly.kitTypeLegend')}
          options={KIT_TYPES}
          labels={kitTypeLabels}
          value={assembly.kit_type}
          onPick={(v) => onPatchAssembly({ kit_type: v })}
          errors={errors}
          disabled={disabled}
        />
      )}
    </>
  );
}
